import React, { useState, useEffect } from 'react';
import { Input, Button, Select, Tag, Modal, Card, Avatar, Table, message, DatePicker } from 'antd';
import { SearchOutlined, PlusOutlined, UserOutlined, SendOutlined, MessageOutlined } from '@ant-design/icons';
import axios from '../../services/axios';
import dayjs from "dayjs";

const { TextArea } = Input;
const { Option } = Select;

const TicketManagement = () => {
  const [tickets, setTickets] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(false);
  const user = localStorage.getItem('user');
  const userType = localStorage.getItem('userType');
  const projectName = JSON.parse(localStorage.getItem("Project"));

  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'Bug',
    duedate: null,
  });

  const priorityColors = {
    low: 'green',
    medium: 'orange',
    high: 'red'
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesPriority =
      filterPriority === 'all' || ticket.priority === filterPriority;

    const matchesSearch =
      ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.id?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesPriority && matchesSearch;
  });

  const handleCreateTicket = async () => {
    try {
      const payload = {
        username: user,
        projectName: projectName,
        title: newTicket.title,
        description: newTicket.description,
        priority: newTicket.priority,
        category: newTicket.category,
        usertype: userType,
        dueDate: newTicket.dueDate
      };

      const res = await axios.post('/create-ticket', payload);

      setTickets([
        ...tickets,
        { ...res.data.ticket, id: res.data.ticket.code }
      ]);

      setIsCreateModalOpen(false);
      setNewTicket({
        title: '',
        description: '',
        priority: 'medium',
        category: 'Bug',
        dueDate: null,
      });

    }  catch (err) {
        if (err.response && err.response.data && err.response.data.msg) {
          message.warning(err.response.data.msg);
        } else if (err.response && err.response.data && err.response.data.userName) {
          message.warning(err.response.data.userName[0]);
        } else if (err.response && err.response.status === 401) {
          message.error("User not authorized");
        } else {
          message.error("Something went wrong");
        }
      } finally {
        setLoading(false); // Ensure loading is set to false
      }
    };

  useEffect(() => {
    fetchUserTickets();
  }, []); 

  const fetchUserTickets = async () => {
    try {
      const res = await axios.post('/fetch-tickets', {
        projectName: projectName,
        usertype: userType,
      });

      const formatted = res.data.tickets.map(t => ({
        ...t,
        id: t.code
      }));

      setTickets(formatted);

    } catch (error) {
      console.error("Failed to fetch tickets:", error);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;

    try {
      const res = await axios.post("/add-comments", {
        ticketId: selectedTicket.id,
        user,
        text: commentText
      });

      const updated = res.data.ticket;

      const updatedTickets = tickets.map(t =>
        t.id === updated.code ? { ...updated, id: updated.code } : t
      );

      setTickets(updatedTickets);
      setSelectedTicket({ ...updated, id: updated.code });
      setCommentText("");

    } catch (error) {
      console.error("Failed to add comment:", error);
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-yellow-500'
    ];
    const index = name.length % colors.length;
    return colors[index];
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns = [
    {
      title: "Ticket ID",
      dataIndex: "id",
      key: "id",
      render: (id) => <span className="font-semibold text-blue-600">{id}</span>,
      width: 150
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text) => <span className="font-medium">{text}</span>,
      width: 200
    },
    {
      title: "Status",
      dataIndex: "isResolved",
      key: "status",
      render: (value) =>
        value === true ? (
          <Tag color="green">CLOSED</Tag>
        ) : (
          <Tag color="red">OPEN</Tag>
        ),
      width: 100
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (level) => (
        <Tag color={priorityColors[level]}>{level.toUpperCase()}</Tag>
      ),
      width: 100
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (cat) => <Tag>{cat}</Tag>,
      width: 100
    },
    {
      title: "Created On",
      dataIndex: "createdOn",
      key: "createdOn",
      render: (date) => dayjs(date).format("DD-MM-YYYY"),
      width: 100
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (date) => dayjs(date).format("DD-MM-YYYY"),
      width: 100
    },
    {
      title: "Created By",
      dataIndex: "username",
      key: "username",
      render: (name) => (
        <div className="flex items-center gap-2">
          <Avatar icon={<UserOutlined />} size="small" />
          <span>{name}</span>
        </div>
      ),
      width: 200
    },
    {
      title: "Comments",
      key: "comments",
      render: (_, row) => (
        <span className="flex items-center gap-1">
          <MessageOutlined /> {row.comments.length}
        </span>
      ),
      width: 100
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">

        {/* Filters */}
        <Card className="mb-6 shadow-sm border-gray-200">
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
            <Input
              placeholder="Search title..."
              prefix={<SearchOutlined className="text-gray-400" />}
              className="w-full sm:max-w-xs"
              size="large"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <Select
              defaultValue="all"
              size="large"
              className="w-full sm:w-40"
              onChange={setFilterPriority}
            >
              <Option value="all">All Priority</Option>
              <Option value="low">Low</Option>
              <Option value="medium">Medium</Option>
              <Option value="high">High</Option>
            </Select>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => setIsCreateModalOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 shadow-md w-full sm:w-auto"
            >
              New Ticket
            </Button>
          </div>
        </Card>

        <Card className="shadow-sm">
          <Table
            columns={columns}
            dataSource={filteredTickets}
            rowKey="id"
            onRow={(record) => ({
              onClick: () => {
                setSelectedTicket(record);
                setIsModalOpen(true);
              }
            })}
            pagination={{ pageSize: 10 }}
            scroll={{ x: "max-content" }} 
          />
        </Card>

        {filteredTickets.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">No tickets found</p>
          </div>
        )}
      </div>

      <Modal
        title={null}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setCommentText('');
        }}
        footer={null}
        width={800}
        className="ticket-modal"
      >
        {selectedTicket && (
          <div className="flex flex-col h-[80vh] max-h-[700px]">

            {/* Header */}
            <div className="p-6 border-b border-gray-200">

              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1 min-w-0">

                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <Tag color={priorityColors[selectedTicket.priority]} className="text-xs">
                      {selectedTicket.priority.toUpperCase()}
                    </Tag>
                  </div>

                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3">
                    {selectedTicket.title}
                  </h2>

                  <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                    {selectedTicket.description}
                  </p>
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-4 border-t border-gray-100">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Category</p>
                  <Tag color="blue" className="text-xs">{selectedTicket.category}</Tag>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Created</p>
                  <span className="font-medium text-gray-800 text-sm">
                    {selectedTicket.username}
                  </span>
                </div>
              </div>
            </div>

            {/* Comments */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
              <div className="mb-4 flex items-center gap-2">
                <MessageOutlined className="text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Comments ({selectedTicket.comments.length})
                </h3>
              </div>

              <div className="space-y-4">
                {selectedTicket.comments.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageOutlined className="text-4xl text-gray-300 mb-2" />
                    <p className="text-gray-500">No comments yet. Be the first to comment!</p>
                  </div>
                ) : (
                  selectedTicket.comments.map((comment) => (
                    <div key={comment.id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                      <div className="flex items-start gap-3">

                        <Avatar className={`${getAvatarColor(comment.user)} flex-shrink-0`} size="default">
                          {getInitials(comment.user)}
                        </Avatar>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                            <span className="font-semibold text-gray-800 text-sm">
                              {comment.user}
                            </span>
                            <span className="text-xs text-gray-500">{formatDateTime(comment.timestamp)}</span>
                          </div>

                          <p className="text-gray-700 text-sm leading-relaxed break-words">
                            {comment.text}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Add Comment */}
            <div className="p-4 border-t border-gray-200 bg-white">
              <div className="flex gap-3">
                <div className="flex-1">
                  <TextArea
                    placeholder="Please keep the chat respectful..."
                    rows={2}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onPressEnter={(e) => {
                      if (e.ctrlKey || e.metaKey) handleAddComment();
                    }}
                    className="mb-2"
                  />
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Press Ctrl+Enter to send</span>
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={handleAddComment}
                      disabled={!commentText.trim()}
                      className="bg-blue-600"
                    >
                      Send
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Create Ticket Modal */}
      <Modal
        title={<span className="text-xl font-semibold">Create New Ticket</span>}
        open={isCreateModalOpen}
        onCancel={() => setIsCreateModalOpen(false)}
        onOk={handleCreateTicket}
        okText="Create Ticket"
        width={600}
        okButtonProps={{
          className: 'bg-blue-600',
          disabled: !newTicket.title || !newTicket.description
        }}
      >
        <div className="space-y-4 mt-6">

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
            <Input
              placeholder="Enter ticket title"
              size="large"
              value={newTicket.title}
              onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
            <TextArea
              placeholder="Describe the issue or request"
              rows={4}
              value={newTicket.description}
              onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>

            <DatePicker
              className="w-full"
              size="large"
              onChange={(date) =>
                setNewTicket({ ...newTicket, dueDate: date ? date.toISOString() : null })
              }
              value={newTicket.dueDate ? dayjs(newTicket.dueDate) : null}
              format="DD-MM-YYYY"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority *</label>
              <Select
                value={newTicket.priority}
                onChange={(value) => setNewTicket({ ...newTicket, priority: value })}
                className="w-full"
                size="large"
              >
                <Option value="low">Low</Option>
                <Option value="medium">Medium</Option>
                <Option value="high">High</Option>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
              <Select
                value={newTicket.category}
                onChange={(value) => setNewTicket({ ...newTicket, category: value })}
                className="w-full"
                size="large"
              >
                <Option value="Bug">Bug</Option>
                <Option value="Feature">Feature</Option>
                <Option value="Task">Task</Option>
                <Option value="Documentation">Documentation</Option>
              </Select>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TicketManagement;